import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";

import {
  Dialog,
  DialogFooter,
  DialogHeader,
  ErrorState,
  ForbiddenState,
  Icon,
  StateMessage,
} from "@/ds";
import { TipoObraTag } from "@/shared/components";
import {
  CAMPO_REFERENCIA_MENSALIZACAO,
  CAMPO_NOME,
  CAMPO_ORIGEM,
  CARREGANDO_LABEL,
  COLETOR_COLUNA,
  COTACAO_NOTA,
  COTACAO_TITLE,
  DETALHE_TITLE,
  DISTRIBUICAO_TITLE,
  EDITAR_LABEL,
  FECHAR_LABEL,
  PROJECAO_TITLE,
  SEM_PROJECAO,
  TABELA_LABEL,
  TOTAL_COLUNA,
  TOTAL_ESCOPO_TITLE,
} from "./detalhe-escopo-macro.constants";
import { FecharTooltip, fecharPt } from "../fechar-tooltip";
import { EXPANDIR_LABEL, RECOLHER_LABEL, TOOLTIP_ICONE } from "../planejamento-controle.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./detalhe-escopo-macro.module.scss";
import type {
  CategoriaDetalhe,
  DetalheEscopoMacroViewModel,
  VariacaoCusto,
} from "./types-detalhe-escopo-macro";

const FECHAR_TESTID = "detalhe-escopo-fechar-x";
const DIALOGO_PT = { footer: { className: modal.dialogoRodape }, ...fecharPt(FECHAR_TESTID) };

function Variacao({ variacao }: { variacao: VariacaoCusto }) {
  return (
    <Tag value={variacao.texto} severity={variacao.tom === "negativo" ? "success" : "danger"} />
  );
}

/** Par rótulo (13, muted) e valor (14 SemiBold) do Figma (`info-*`). */
function Info({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className={styles.info}>
      <span className={styles.rotulo}>{rotulo}</span>
      <span className={styles.valorInfo}>{valor}</span>
    </div>
  );
}

function CategoriaCard({
  categoria,
  rotuloComFator,
  testId,
}: {
  categoria: CategoriaDetalhe;
  rotuloComFator: string;
  testId: string;
}) {
  return (
    <section className={styles.card} data-testid={testId}>
      <h3 className={styles.cardTitulo}>{categoria.titulo}</h3>
      <Info rotulo={`Valor Projetado para ${categoria.titulo}`} valor={categoria.valor} />
      {categoria.conversao || categoria.variacao ? (
        <div className={styles.linha}>
          {categoria.conversao ? (
            <span className={styles.rotulo}>{categoria.conversao}</span>
          ) : null}
          {categoria.variacao ? <Variacao variacao={categoria.variacao} /> : null}
        </div>
      ) : null}
      {categoria.comFator ? (
        <span className={styles.texto13}>
          {rotuloComFator}: {categoria.comFator}
        </span>
      ) : null}
      <Info
        rotulo={`Distribuição por coletor (${categoria.titulo})`}
        valor={categoria.distribuicao}
      />
      <Info rotulo={`Curva Mensalização (${categoria.titulo})`} valor={categoria.curva} />
    </section>
  );
}

/** Detalhe do Escopo Macro em modal, somente leitura (Figma 11921:154757): identificação, cotação, custo padrão, fator, projeção e a tabela de coletores com a mensalização. */
export function DetalheEscopoMacroView({
  estado,
  errorMessage,
  recarregar,
  fechar,
  editar,
  cabecalho,
  campos,
  cotacao,
  custoPadrao,
  fatorIdade,
  rotuloComFator,
  servicos,
  materiais,
  total,
  avisoSemProjecao,
  notaColetores,
  competencias,
  linhas,
  colunasTabela,
  alternarCategoria,
}: DetalheEscopoMacroViewModel) {
  return (
    <Dialog
      visible
      onHide={fechar}
      size="xl"
      className="p-dialog-ficha"
      // Figma 16417:421252 (28/10): só o título e o X, sem a seta de voltar da versão anterior.
      header={<DialogHeader title={DETALHE_TITLE} />}
      pt={DIALOGO_PT}
      footer={
        <DialogFooter
          className={modal.rodape}
          cancelLabel={FECHAR_LABEL}
          onCancel={fechar}
          confirmLabel={EDITAR_LABEL}
          onConfirm={editar.abrir}
          confirmButtonProps={{
            disabled: editar.desabilitado,
            tooltip: editar.desabilitado ? (editar.motivo ?? undefined) : undefined,
            icon: <Icon name="pencil" size={14} />,
            iconPos: "left",
          }}
          testId="detalhe-escopo"
        />
      }
      focusOnShow={false}
      data-testid="detalhe-escopo-macro"
    >
      <FecharTooltip testId={FECHAR_TESTID} />
      {estado === "carregando" ? (
        <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="detalhe-escopo-loading" />
      ) : null}
      {estado === "proibido" ? (
        <ForbiddenState description={errorMessage ?? undefined} testId="detalhe-escopo-error" />
      ) : null}
      {estado === "erro" ? (
        <ErrorState
          message={errorMessage ?? undefined}
          onRetry={recarregar}
          testId="detalhe-escopo-error"
        />
      ) : null}

      {cabecalho && campos && (estado === "pronto" || estado === "sem-projecao") ? (
        <div className={styles.corpo}>
          <div className={styles.escopo}>
            <div className={styles.identificacao}>
              <TipoObraTag tipo={cabecalho.codigo} />
              <div className={styles.identificacaoTextos}>
                <strong className={styles.nome}>{cabecalho.nome}</strong>
                <span className={styles.subtitulo}>{cabecalho.subtitulo}</span>
              </div>
              {estado === "sem-projecao" ? (
                <Tag value={SEM_PROJECAO} severity="info" data-testid="detalhe-escopo-pendente" />
              ) : null}
              {cabecalho.aguardandoAprovacao ? (
                <Tag
                  value={cabecalho.aguardandoAprovacao}
                  severity="warning"
                  data-testid="detalhe-escopo-aguardando-aprovacao"
                />
              ) : null}
              {cabecalho.classificacao ? (
                <Tag
                  value={cabecalho.classificacao}
                  severity="info"
                  data-testid="detalhe-escopo-classificacao"
                />
              ) : null}
            </div>

            <dl className={styles.campos}>
              <div className={styles.info}>
                <dt className={styles.rotulo}>{CAMPO_NOME}</dt>
                <dd className={styles.valorInfo}>{campos.nome}</dd>
              </div>
              <div className={styles.info}>
                <dt className={styles.rotulo}>{CAMPO_ORIGEM}</dt>
                <dd className={styles.valorInfo}>{campos.origem}</dd>
              </div>
              <div className={styles.info}>
                <dt className={styles.rotulo}>{CAMPO_REFERENCIA_MENSALIZACAO}</dt>
                <dd className={styles.valorInfo}>{campos.referenciaMensalizacao}</dd>
              </div>
            </dl>
          </div>

          {estado === "sem-projecao" ? (
            <Message
              severity="info"
              className="p-message-simple p-message-block"
              icon={<Icon name="info-circle" size={18} />}
              text={avisoSemProjecao}
              data-testid="detalhe-escopo-sem-projecao"
            />
          ) : null}

          {cotacao ? (
            <div className={styles.cotacao} data-testid="detalhe-escopo-cotacao">
              <Icon name="dollar" size={14} />
              <div className={styles.cotacaoTextos}>
                <span className={styles.cotacaoTitulo}>{COTACAO_TITLE}</span>
                <span className={styles.cotacaoValor}>{cotacao}</span>
              </div>
              <span className={styles.cotacaoNota}>{COTACAO_NOTA}</span>
            </div>
          ) : null}

          {custoPadrao ? (
            <div className={styles.bloco} data-testid="detalhe-escopo-custo-padrao">
              <span className={styles.blocoTitulo}>Custo padrão</span>
              <span>{custoPadrao}</span>
            </div>
          ) : null}
          {fatorIdade ? (
            <div className={styles.bloco} data-testid="detalhe-escopo-fator">
              <span className={styles.blocoTitulo}>Fator de idade da embarcação</span>
              <span className={styles.rotulo}>{fatorIdade}</span>
            </div>
          ) : null}

          {estado === "pronto" || estado === "sem-projecao" ? (
            <>
              <div className={styles.grupo}>
                <h2 className={styles.secao}>{PROJECAO_TITLE}</h2>
                <div className={styles.cards}>
                  {servicos ? (
                    <CategoriaCard
                      categoria={servicos}
                      rotuloComFator={rotuloComFator}
                      testId="detalhe-escopo-servicos"
                    />
                  ) : null}
                  {materiais ? (
                    <CategoriaCard
                      categoria={materiais}
                      rotuloComFator={rotuloComFator}
                      testId="detalhe-escopo-materiais"
                    />
                  ) : null}
                  {total ? (
                    <section className={styles.cardTotal} data-testid="detalhe-escopo-total">
                      <h3 className={styles.cardTotalTitulo}>{TOTAL_ESCOPO_TITLE}</h3>
                      <span className={styles.totalValor}>{total.valor}</span>
                      {total.projetado || total.fator ? (
                        <div className={styles.composicao}>
                          {total.projetado ? <span>Projetado: {total.projetado}</span> : null}
                          {total.fator ? <span>{total.fator}</span> : null}
                        </div>
                      ) : null}
                      {total.variacao ? <Variacao variacao={total.variacao} /> : null}
                    </section>
                  ) : null}
                </div>
              </div>

              <div className={styles.grupoColetores}>
                <h2 className={styles.secao}>{DISTRIBUICAO_TITLE}</h2>
                {estado === "pronto" ? (
                  <div className={styles.tabela} role="table" aria-label={TABELA_LABEL}>
                    <div
                      className={classNames(styles.linhaTabela, styles.cabecalhoTabela)}
                      style={{ gridTemplateColumns: `${colunasTabela} minmax(0, 1fr)` }}
                      role="row"
                    >
                      <span className={styles.coletor} role="columnheader">
                        {COLETOR_COLUNA}
                      </span>
                      <span className={styles.numero} role="columnheader">
                        {TOTAL_COLUNA}
                      </span>
                      {competencias.map((competencia) => (
                        <span key={competencia} className={styles.numero} role="columnheader">
                          {competencia}
                        </span>
                      ))}
                      <span aria-hidden="true" />
                    </div>
                    {linhas.map((linha) => (
                      <div
                        key={linha.key}
                        className={classNames(styles.linhaTabela, styles[linha.nivel])}
                        style={{ gridTemplateColumns: `${colunasTabela} minmax(0, 1fr)` }}
                        role="row"
                        data-testid={linha.testId}
                      >
                        <span className={styles.coletor} role="cell">
                          {linha.expansivel ? (
                            <Button
                              type="button"
                              text
                              rounded
                              severity="secondary"
                              className="p-button-row-actions"
                              icon={
                                <Icon
                                  name={linha.expandida ? "chevron-down" : "chevron-right"}
                                  size={14}
                                />
                              }
                              aria-label={`${linha.expandida ? RECOLHER_LABEL : EXPANDIR_LABEL} ${linha.nome}`}
                              tooltip={linha.expandida ? RECOLHER_LABEL : EXPANDIR_LABEL}
                              tooltipOptions={TOOLTIP_ICONE}
                              aria-expanded={linha.expandida}
                              onClick={() => alternarCategoria(linha.key)}
                            />
                          ) : null}
                          <span className={styles.coletorNome}>{linha.nome}</span>
                        </span>
                        <span className={styles.numero} role="cell">
                          {linha.total}
                        </span>
                        {linha.meses.map((mes, indice) => (
                          <span key={competencias[indice]} className={styles.numero} role="cell">
                            {mes}
                          </span>
                        ))}
                        <span aria-hidden="true" />
                      </div>
                    ))}
                  </div>
                ) : null}
                <p className={styles.nota}>{notaColetores}</p>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
